// components/applicationProfile/common/navigation.ts

import { getFirstStep, getFlow, type ApplicationType, type FlowOptions } from './flow';

export type QueryShape = Record<string, string | undefined>;

/**
 * Screens that exist outside the flow's substep lists.
 *
 * The post-submit confirmation is reached by setting `substep=success`, which is
 * deliberately not a member of any step's substeps — the shell renders it as a
 * standalone screen with no stepper. Step-validity coercion has to know about
 * it, or it reads as a stale URL and redirects the renter off the confirmation
 * they just earned (their application submitted fine; only the screen was lost).
 */
function isOutOfFlowScreen(tab: string, substep: string) {
  return tab === 'confirmApply' && substep === 'success';
}

function equalSearchParams(a: URLSearchParams, b: URLSearchParams) {
  const norm = (sp: URLSearchParams) =>
    Array.from(sp.entries())
      .sort(([ka], [kb]) => ka.localeCompare(kb))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
  return norm(a) === norm(b);
}

export function setQueryParams(params: URLSearchParams, updates: QueryShape): URLSearchParams {
  const next = new URLSearchParams(params.toString());

  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  });

  return next;
}

/**
 * Ensures profile/application-profile always has
 * tab + substep + applicationType
 */
export function ensureApplicationProfileQuery(query: QueryShape) {
  const applicationType = (query.applicationType as ApplicationType) || 'personal';

  let { tab, substep } = query;

  const opts: FlowOptions = {
    unit: query.unit ?? null,
    partner: query.partner ?? null,
    editId: query.editId ?? null,
    isCoApplicant: query.isCoApplicant === 'true',
  };

  if (!tab || !substep) {
    const first = getFirstStep(applicationType, opts);
    tab = first.tab;
    substep = first.substep;
  } else if (!isOutOfFlowScreen(tab, substep)) {
    // The step must exist in THIS flow. Applicant profile has no Confirm and
    // apply, so a stale URL pointing at it (an old bookmark, or a session that
    // got that far before the split) would otherwise render a step the stepper
    // no longer lists. Fall back to the start of the flow.
    const flow = getFlow(applicationType, opts);
    const step = flow.find(s => s.tab === tab);
    if (!step) {
      const first = getFirstStep(applicationType, opts);
      tab = first.tab;
      substep = first.substep;
    } else if (!step.substeps.some(s => s.id === substep)) {
      substep = step.substeps[0]!.id;
    }
  }

  const next = new URLSearchParams();

  Object.entries({
    ...query,
    tab,
    substep,
    applicationType,
  }).forEach(([k, v]) => {
    if (v) next.set(k, v);
  });

  // Only redirect when we actually changed/added required params.
  const current = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v) current.set(k, v);
  });

  const destination = `/profile/application-profile?${next.toString()}`;
  if (equalSearchParams(current, next)) {
    return { shouldRedirect: false, destination };
  }

  return {
    shouldRedirect: true,
    destination,
  };
}

/**
 * Convenience helper to build navigation URLs
 */
export function buildApplicationProfileUrl(params: QueryShape) {
  const sp = new URLSearchParams();

  Object.entries(params).forEach(([k, v]) => {
    if (v) sp.set(k, v);
  });

  return `/profile/application-profile?${sp.toString()}`;
}
